import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { subscriptionApi, documentApi, subscriptionPlanApi, profileApi } from '../services/api';
import { Check, Xmark, Download, Trash, Settings, User, Lock, Camera } from 'iconoir-react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/account-management.css';

interface Subscription {
  id: number;
  plan: any;
  billing_period: 'monthly' | 'yearly';
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancelled_at: string | null;
  is_active: boolean;
}

interface Document {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  formatted_size: string;
  file_type: string;
  created_at: string;
  board_name: string;
  card_title: string;
  path: string;
}

interface Usage {
  boards_count: number;
  cards_count: number;
  storage_gb: number;
}

interface Overage {
  limit: number;
  used: number;
  overage: number;
  price_per_unit: number;
  unit_label: string;
}

type TabType = 'profile' | 'subscription' | 'overages' | 'documents';

export const AccountManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [overages, setOverages] = useState<any>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentStats, setDocumentStats] = useState<any>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<'created_at' | 'original_filename' | 'size' | 'mime_type'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<string>('');

  // Profile states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState('');

  useEffect(() => {
    loadData();
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
  }, [activeTab, sortBy, sortOrder, filterType]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subResponse, plansResponse] = await Promise.all([
        subscriptionApi.getCurrent(),
        subscriptionPlanApi.getAll()
      ]);
      
      setSubscription(subResponse.data.subscription);
      setAvailablePlans(plansResponse.data.plans);

      if (activeTab === 'overages') {
        await loadUsageAndOverages();
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageAndOverages = async () => {
    try {
      const response = await subscriptionApi.getUsage();
      setUsage(response.data.usage);
      setOverages(response.data.overages);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisation:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await documentApi.getAll({ sort_by: sortBy, sort_order: sortOrder, filter_type: filterType });
      setDocuments(response.data.documents);
      setDocumentStats(response.data.stats);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    }
  };

  const handleChangePlan = async (planId: number, billingPeriod: 'monthly' | 'yearly') => {
    if (!window.confirm('Êtes-vous sûr de vouloir changer de plan ?')) {
      return;
    }

    try {
      const response = await subscriptionApi.changePlan(planId, billingPeriod);
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      // Si le code est 402 (Payment Required), rediriger vers le paiement
      if (error.response?.status === 402) {
        const { plan, billing_period } = error.response.data;
        
        if (window.confirm('Un paiement est requis pour ce plan. Souhaitez-vous procéder au paiement ?')) {
          navigate('/upgrade-plan', { 
            state: { 
              planId: plan.id, 
              billingPeriod: billing_period
            } 
          });
        }
      } else {
        alert(error.response?.data?.message || 'Erreur lors du changement de plan');
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ?')) {
      return;
    }

    try {
      const response = await subscriptionApi.cancel();
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await subscriptionApi.reactivate();
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la réactivation');
    }
  };

  const handleDeleteDocument = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await documentApi.delete(id);
      await loadDocuments();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedDocuments.length === 0) {
      alert('Aucun document sélectionné');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedDocuments.length} document(s) ?`)) {
      return;
    }

    try {
      await documentApi.deleteMultiple(selectedDocuments);
      setSelectedDocuments([]);
      await loadDocuments();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const toggleDocumentSelection = (id: number) => {
    setSelectedDocuments(prev =>
      prev.includes(id) ? prev.filter(docId => docId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  // Profile handlers
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setIsUpdatingProfile(true);

    try {
      // C01 — Sanctum SPA : api (withCredentials) au lieu d'axios + Bearer
      await api.put('/profile', { name, email });

      setProfileSuccess('Profil mis à jour avec succès !');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err: any) {
      setProfileError(err.response?.data?.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await api.put('/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      setPasswordSuccess('Mot de passe mis à jour avec succès !');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setAvatarError('Le fichier doit être une image');
      return;
    }

    setAvatarFile(file);
    setAvatarError('');

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const refreshUserData = async () => {
    try {
      await api.get('/me');
      window.location.reload();
    } catch (err) {
      console.error('Erreur lors du rechargement des données utilisateur', err);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;

    setIsUploadingAvatar(true);
    setAvatarError('');
    setAvatarSuccess('');

    try {
      await profileApi.uploadAvatar(avatarFile);
      setAvatarSuccess('Avatar mis à jour avec succès !');
      setAvatarFile(null);
      setAvatarPreview(null);
      
      await refreshUserData();
      
      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err: any) {
      setAvatarError(err.response?.data?.message || 'Erreur lors de l\'upload de l\'avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer votre avatar ?')) return;

    setIsUploadingAvatar(true);
    setAvatarError('');

    try {
      await profileApi.deleteAvatar();
      setAvatarSuccess('Avatar supprimé avec succès !');
      
      await refreshUserData();
      
      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err: any) {
      setAvatarError(err.response?.data?.message || 'Erreur lors de la suppression de l\'avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div className="account-management-page">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="account-management-page">
      <div className="account-header">
        <h1>Gestion de mon compte</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          Retour au dashboard
        </button>
      </div>

      <div className="account-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User width={20} height={20} />
          Profil
        </button>
        <button
          className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          <Settings width={20} height={20} />
          Abonnement
        </button>
        <button
          className={`tab ${activeTab === 'overages' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('overages');
            loadUsageAndOverages();
          }}
        >
          Dépassements
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
      </div>

      <div className="account-content">
        {activeTab === 'profile' && user && (
          <ProfileTab
            user={user}
            name={name}
            email={email}
            currentPassword={currentPassword}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            avatarFile={avatarFile}
            avatarPreview={avatarPreview}
            isUpdatingProfile={isUpdatingProfile}
            isUpdatingPassword={isUpdatingPassword}
            isUploadingAvatar={isUploadingAvatar}
            profileError={profileError}
            passwordError={passwordError}
            avatarError={avatarError}
            profileSuccess={profileSuccess}
            passwordSuccess={passwordSuccess}
            avatarSuccess={avatarSuccess}
            onNameChange={setName}
            onEmailChange={setEmail}
            onCurrentPasswordChange={setCurrentPassword}
            onNewPasswordChange={setNewPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onAvatarChange={handleAvatarChange}
            onUpdateProfile={handleUpdateProfile}
            onUpdatePassword={handleUpdatePassword}
            onUploadAvatar={handleUploadAvatar}
            onDeleteAvatar={handleDeleteAvatar}
          />
        )}

        {activeTab === 'subscription' && subscription && (
          <SubscriptionTab
            subscription={subscription}
            availablePlans={availablePlans}
            onChangePlan={handleChangePlan}
            onCancel={handleCancelSubscription}
            onReactivate={handleReactivateSubscription}
          />
        )}

        {activeTab === 'overages' && usage && overages && (
          <OveragesTab usage={usage} overages={overages} plan={subscription?.plan} />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab
            documents={documents}
            stats={documentStats}
            selectedDocuments={selectedDocuments}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filterType={filterType}
            onSortChange={(field) => {
              if (sortBy === field) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              } else {
                setSortBy(field);
                setSortOrder('desc');
              }
            }}
            onFilterChange={setFilterType}
            onToggleSelection={toggleDocumentSelection}
            onToggleSelectAll={toggleSelectAll}
            onDelete={handleDeleteDocument}
            onDeleteMultiple={handleDeleteMultiple}
          />
        )}
      </div>
    </div>
  );
};

// Profile Tab Component
interface ProfileTabProps {
  user: any;
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  avatarFile: File | null;
  avatarPreview: string | null;
  isUpdatingProfile: boolean;
  isUpdatingPassword: boolean;
  isUploadingAvatar: boolean;
  profileError: string;
  passwordError: string;
  avatarError: string;
  profileSuccess: string;
  passwordSuccess: string;
  avatarSuccess: string;
  onNameChange: (name: string) => void;
  onEmailChange: (email: string) => void;
  onCurrentPasswordChange: (password: string) => void;
  onNewPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdateProfile: (e: React.FormEvent) => void;
  onUpdatePassword: (e: React.FormEvent) => void;
  onUploadAvatar: () => void;
  onDeleteAvatar: () => void;
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  user,
  name,
  email,
  currentPassword,
  newPassword,
  confirmPassword,
  avatarFile,
  avatarPreview,
  isUpdatingProfile,
  isUpdatingPassword,
  isUploadingAvatar,
  profileError,
  passwordError,
  avatarError,
  profileSuccess,
  passwordSuccess,
  avatarSuccess,
  onNameChange,
  onEmailChange,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onAvatarChange,
  onUpdateProfile,
  onUpdatePassword,
  onUploadAvatar,
  onDeleteAvatar
}) => {
  return (
    <div className="profile-tab">
      <h2>Informations personnelles</h2>

      {/* Avatar Section */}
      <div className="avatar-section">
        <div className="avatar-preview">
          {avatarPreview ? (
            <img src={avatarPreview} alt="Aperçu avatar" className="avatar-image" />
          ) : user.avatar_url ? (
            <img src={user.avatar_url} alt="Avatar actuel" className="avatar-image" />
          ) : (
            <div className="avatar-placeholder">
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="avatar-actions">
          <label htmlFor="avatar-upload" className="btn btn-secondary btn-sm">
            <Camera width={16} height={16} />
            Choisir une image
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={onAvatarChange}
            style={{ display: 'none' }}
          />
          
          {avatarFile && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onUploadAvatar}
              disabled={isUploadingAvatar}
            >
              <Check width={16} height={16} />
              {isUploadingAvatar ? 'Upload...' : 'Enregistrer'}
            </button>
          )}
          
          {user.avatar_url && !avatarFile && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={onDeleteAvatar}
              disabled={isUploadingAvatar}
            >
              <Trash width={16} height={16} />
              Supprimer
            </button>
          )}
        </div>
        
        <div className="avatar-info">
          <small>Format: JPG, PNG, GIF - Taille max: 2 Mo</small>
        </div>
        
        {avatarError && <div className="settings-error">{avatarError}</div>}
        {avatarSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {avatarSuccess}
          </div>
        )}
      </div>

      {/* Profile Form */}
      <div className="profile-form-section">
        <h3>Informations du compte</h3>
        {profileError && <div className="settings-error">{profileError}</div>}
        {profileSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {profileSuccess}
          </div>
        )}

        <form onSubmit={onUpdateProfile} className="settings-form">
          <div className="form-group">
            <label htmlFor="name">Nom complet</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              required
              disabled={isUpdatingProfile}
              placeholder="Votre nom"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              required
              disabled={isUpdatingProfile}
              placeholder="votre@email.com"
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isUpdatingProfile}
          >
            {isUpdatingProfile ? 'Mise à jour...' : 'Mettre à jour le profil'}
          </button>
        </form>
      </div>

      {/* Password Form */}
      <div className="password-form-section">
        <h3>Changer le mot de passe</h3>
        {passwordError && <div className="settings-error">{passwordError}</div>}
        {passwordSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={onUpdatePassword} className="settings-form">
          <div className="form-group">
            <label htmlFor="current-password">Mot de passe actuel</label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => onCurrentPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
            />
          </div>

          <div className="form-group">
            <label htmlFor="new-password">Nouveau mot de passe</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
              minLength={8}
            />
            <small>Au moins 8 caractères</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirmer le nouveau mot de passe</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isUpdatingPassword}
          >
            {isUpdatingPassword ? 'Mise à jour...' : 'Changer le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Subscription Tab Component
interface SubscriptionTabProps {
  subscription: Subscription;
  availablePlans: any[];
  onChangePlan: (planId: number, billingPeriod: 'monthly' | 'yearly') => void;
  onCancel: () => void;
  onReactivate: () => void;
}

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({
  subscription,
  availablePlans,
  onChangePlan,
  onCancel,
  onReactivate
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'yearly'>(subscription.billing_period);

  return (
    <div className="subscription-tab">
      <div className="current-subscription">
        <h2>Abonnement actuel</h2>
        <div className="subscription-card">
          <div className="subscription-info">
            <h3>{subscription.plan.display_name}</h3>
            <p className="subscription-status">
              Statut: <span className={`status-badge ${subscription.status}`}>{subscription.status}</span>
            </p>
            <p>Période: {subscription.billing_period === 'monthly' ? 'Mensuel' : 'Annuel'}</p>
            <p>Début: {new Date(subscription.current_period_start).toLocaleDateString()}</p>
            <p>Fin: {new Date(subscription.current_period_end).toLocaleDateString()}</p>
            {subscription.cancelled_at && (
              <p className="cancelled">Annulé le: {new Date(subscription.cancelled_at).toLocaleDateString()}</p>
            )}
          </div>
          <div className="subscription-actions">
            {/* Plan gratuit : pas d'annulation/réactivation (toujours actif).
                Pour quitter Ventilio, l'utilisateur supprime son compte. */}
            {!subscription.plan.has_ads && (
              subscription.cancelled_at ? (
                <button className="btn btn-primary" onClick={onReactivate}>
                  Réactiver l'abonnement
                </button>
              ) : (
                <button className="btn btn-danger" onClick={onCancel}>
                  Annuler l'abonnement
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="available-plans">
        <h2>Changer de plan</h2>
        <div className="billing-period-selector">
          <button
            className={`period-btn ${selectedPeriod === 'monthly' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('monthly')}
          >
            Mensuel
          </button>
          <button
            className={`period-btn ${selectedPeriod === 'yearly' ? 'active' : ''}`}
            onClick={() => setSelectedPeriod('yearly')}
          >
            Annuel
          </button>
        </div>

        <div className="plans-grid">
          {availablePlans.map(plan => {
            const price = selectedPeriod === 'monthly' ? plan.price_monthly : plan.price_yearly;
            const displayPrice = typeof price === 'number' ? price : parseFloat(price || '0');
            const isCurrent = plan.id === subscription.plan.id && selectedPeriod === subscription.billing_period;

            return (
              <div key={plan.id} className={`plan-card ${isCurrent ? 'current' : ''}`}>
                <h3>{plan.display_name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="plan-price">
                  <span className="price">{displayPrice.toFixed(2)}€</span>
                  <span className="period">/{selectedPeriod === 'monthly' ? 'mois' : 'an'}</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature: string, index: number) => (
                    <li key={index}>
                      <Check width={16} height={16} /> {feature}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button className="btn btn-secondary" disabled>
                    Plan actuel
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => onChangePlan(plan.id, selectedPeriod)}
                  >
                    Choisir ce plan
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Overages Tab Component
interface OveragesTabProps {
  usage: Usage;
  overages: any;
  plan: any;
}

const OveragesTab: React.FC<OveragesTabProps> = ({ usage, overages, plan }) => {
  return (
    <div className="overages-tab">
      <h2>Utilisation et dépassements</h2>
      
      <div className="usage-cards">
        <div className="usage-card">
          <h3>Tableaux</h3>
          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{ width: `${Math.min((usage.boards_count / overages.boards.limit) * 100, 100)}%` }}
            />
          </div>
          <p>{usage.boards_count} / {overages.boards.limit}</p>
          {overages.boards.overage > 0 && (
            <p className="overage-warning">
              Dépassement: {overages.boards.overage} tableaux
              <br />
              Coût: {(Math.ceil(overages.boards.overage / 10) * overages.boards.price_per_unit).toFixed(2)}€
            </p>
          )}
        </div>

        <div className="usage-card">
          <h3>Cartes</h3>
          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{ width: `${Math.min((usage.cards_count / overages.cards.limit) * 100, 100)}%` }}
            />
          </div>
          <p>{usage.cards_count} / {overages.cards.limit}</p>
          {overages.cards.overage > 0 && (
            <p className="overage-warning">
              Dépassement: {overages.cards.overage} cartes
              <br />
              Coût: {(Math.ceil(overages.cards.overage / 1000) * overages.cards.price_per_unit).toFixed(2)}€
            </p>
          )}
        </div>

        <div className="usage-card">
          <h3>Stockage</h3>
          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{ width: `${Math.min((overages.storage.used / overages.storage.limit) * 100, 100)}%` }}
            />
          </div>
          <p>{overages.storage.used.toFixed(2)} MB / {overages.storage.limit} MB</p>
          {overages.storage.overage > 0 && (
            <p className="overage-warning">
              Dépassement: {overages.storage.overage.toFixed(2)} MB
              <br />
              Coût: {(Math.ceil(overages.storage.overage / 1024) * overages.storage.price_per_unit).toFixed(2)}€
            </p>
          )}
        </div>
      </div>

      <div className="overage-info">
        <h3>Tarification des dépassements</h3>
        <ul>
          <li>{plan.price_per_10_extra_boards}€ par tranche de 10 tableaux supplémentaires</li>
          <li>{plan.price_per_1000_extra_cards}€ par tranche de 1000 cartes supplémentaires</li>
          <li>{plan.price_per_extra_gb}€ par Go de stockage supplémentaire</li>
        </ul>
      </div>
    </div>
  );
};

// Documents Tab Component
interface DocumentsTabProps {
  documents: Document[];
  stats: any;
  selectedDocuments: number[];
  sortBy: string;
  sortOrder: string;
  filterType: string;
  onSortChange: (field: any) => void;
  onFilterChange: (type: string) => void;
  onToggleSelection: (id: number) => void;
  onToggleSelectAll: () => void;
  onDelete: (id: number) => void;
  onDeleteMultiple: () => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  stats,
  selectedDocuments,
  sortBy,
  sortOrder,
  filterType,
  onSortChange,
  onFilterChange,
  onToggleSelection,
  onToggleSelectAll,
  onDelete,
  onDeleteMultiple
}) => {
  return (
    <div className="documents-tab">
      <div className="documents-header">
        <h2>Mes documents</h2>
        {stats && (
          <div className="documents-stats">
            <span>{stats.total_count} documents</span>
            <span>{stats.total_size_formatted}</span>
          </div>
        )}
      </div>

      <div className="documents-controls">
        <div className="filter-controls">
          <select value={filterType} onChange={(e) => onFilterChange(e.target.value)}>
            <option value="">Tous les types</option>
            <option value="image">Images ({stats?.by_type.images || 0})</option>
            <option value="video">Vidéos ({stats?.by_type.videos || 0})</option>
            <option value="document">Documents ({stats?.by_type.documents || 0})</option>
            <option value="archive">Archives ({stats?.by_type.archives || 0})</option>
          </select>

          <select value={sortBy} onChange={(e) => onSortChange(e.target.value)}>
            <option value="created_at">Date d'ajout</option>
            <option value="original_filename">Nom</option>
            <option value="size">Taille</option>
            <option value="mime_type">Type</option>
          </select>

          <button
            className="btn btn-secondary"
            onClick={() => onSortChange(sortBy)}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <div className="bulk-actions">
          {selectedDocuments.length > 0 && (
            <button className="btn btn-danger" onClick={onDeleteMultiple}>
              <Trash width={18} height={18} />
              Supprimer ({selectedDocuments.length})
            </button>
          )}
        </div>
      </div>

      <div className="documents-list">
        <div className="documents-list-header">
          <input
            type="checkbox"
            checked={selectedDocuments.length === documents.length && documents.length > 0}
            onChange={onToggleSelectAll}
          />
          <span>Nom</span>
          <span>Tableau</span>
          <span>Carte</span>
          <span>Taille</span>
          <span>Date</span>
          <span>Actions</span>
        </div>

        {documents.map(doc => (
          <div key={doc.id} className="document-row">
            <input
              type="checkbox"
              checked={selectedDocuments.includes(doc.id)}
              onChange={() => onToggleSelection(doc.id)}
            />
            <span className="document-name">{doc.filename}</span>
            <span>{doc.board_name}</span>
            <span>{doc.card_title}</span>
            <span>{doc.formatted_size}</span>
            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
            <div className="document-actions">
              <button
                className="btn-icon"
                onClick={() => onDelete(doc.id)}
                title="Supprimer"
              >
                <Trash width={18} height={18} />
              </button>
            </div>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="no-documents">
            Aucun document trouvé
          </div>
        )}
      </div>
    </div>
  );
};
